/**
 * Recipe → primitives. Pure: no `three` import (the web adapter
 * `web/src/components/room/gear/productMesh.ts` turns primitives into meshes).
 *
 * Invariant: the union of all primitives' bounds equals the recipe body's
 * box exactly. Features that stand proud of a face are accommodated by
 * pulling the cabinet box in by that face's largest protrusion, so a
 * product's model is always exactly its published W × H × D.
 */
import type { Face, Recipe, RecipeFeature, RecipeMaterial } from '@roomtreatment/av-catalog';

export type Vec3 = [number, number, number];

/** Local frame of a primitive: columns x, y, z of a rotation matrix (unit vectors). */
export interface Basis {
  x: Vec3;
  y: Vec3;
  z: Vec3;
}

export type Primitive =
  | {
      shape: 'roundedBox';
      /** Extents along the basis x, y, z axes. */
      size: Vec3;
      /** Rounds every edge. */
      radius: number;
      /** Rounds only the four edges parallel to the basis y axis. */
      sideRadius: number;
      /** Centre of the box. */
      at: Vec3;
      basis: Basis;
      material: RecipeMaterial;
    }
  | {
      shape: 'lathe';
      /** Profile [radius, height] revolved about the basis y axis, from `at`. */
      points: [number, number][];
      /** Revolution segments (default 32) and start angle in radians (default 0).
       *  4 segments from π/4 makes a square frustum, e.g. a horn flare. */
      segments?: number;
      phiStart?: number;
      at: Vec3;
      basis: Basis;
      material: RecipeMaterial;
    }
  | {
      shape: 'prism';
      /** Closed profile in the basis x/y plane, extruded `depth` along basis z from `at`. */
      points: [number, number][];
      depth: number;
      at: Vec3;
      basis: Basis;
      material: RecipeMaterial;
    };

export interface BuiltRecipe {
  primitives: Primitive[];
  /** Overall box, mm: [w, h, d]. Equals the body by construction. */
  size: Vec3;
}

const DARK: RecipeMaterial = { color: '#0d0d0d', finish: 'matte' };
const SURROUND: RecipeMaterial = { color: '#141414', finish: 'satin' };

interface FaceFrame {
  /** Point on the face at u = 0, v = 0. */
  origin: Vec3;
  u: Vec3;
  v: Vec3;
  n: Vec3;
  width: number;
  height: number;
}

const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const scale = (a: Vec3, s: number): Vec3 => [a[0] * s, a[1] * s, a[2] * s];
const cross = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];

interface CabinetBox {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  /** Top height at the front and rear edges (differ only for a wedge). */
  yf: number;
  yr: number;
  z0: number;
  z1: number;
}

/** Faces of the cabinet. u/v per the schema's face convention. */
function faceFrame(face: Face, box: CabinetBox): FaceFrame {
  const w = box.x1 - box.x0;
  const d = box.z1 - box.z0;
  const cx = (box.x0 + box.x1) / 2;
  const cz = (box.z0 + box.z1) / 2;
  const side = Math.min(box.yf, box.yr) - box.y0;
  switch (face) {
    case 'front':
      return { origin: [cx, box.y0, box.z1], u: [1, 0, 0], v: [0, 1, 0], n: [0, 0, 1], width: w, height: box.yf - box.y0 };
    case 'rear':
      return { origin: [cx, box.y0, box.z0], u: [-1, 0, 0], v: [0, 1, 0], n: [0, 0, -1], width: w, height: box.yr - box.y0 };
    case 'left':
      return { origin: [box.x0, box.y0, cz], u: [0, 0, 1], v: [0, 1, 0], n: [-1, 0, 0], width: d, height: side };
    case 'right':
      return { origin: [box.x1, box.y0, cz], u: [0, 0, -1], v: [0, 1, 0], n: [1, 0, 0], width: d, height: side };
    case 'top':
      return { origin: [cx, box.y1, box.z0], u: [1, 0, 0], v: [0, 0, 1], n: [0, 1, 0], width: w, height: d };
    case 'slope': {
      const a = box.yr - box.yf;
      const b = box.z0 - box.z1;
      const L = Math.hypot(a, b);
      return { origin: [cx, box.yf, box.z1], u: [1, 0, 0], v: [0, a / L, b / L], n: [0, -b / L, a / L], width: w, height: L };
    }
  }
}

/** Point on a face at schema (u, v), lifted `lift` mm along the outward normal. */
function facePoint(f: FaceFrame, u: number, v: number, lift = 0): Vec3 {
  return add(add(add(f.origin, scale(f.u, u * f.width)), scale(f.v, v * f.height)), scale(f.n, lift));
}

/** Basis whose y axis is the face normal (for lathes). */
const latheBasis = (f: FaceFrame): Basis => ({ x: f.u, y: f.n, z: cross(f.u, f.n) });

/** Half width, half height, and extra width to the right (repeats), in mm on the face. */
export function featureHalfExtents(ft: Exclude<RecipeFeature, { kind: 'feet' }>): [number, number, number] {
  switch (ft.kind) {
    case 'driver': {
      const r = ft.outerMm / 2;
      return ft.mouth ? [Math.max(r, ft.mouth.w / 2), Math.max(r, ft.mouth.h / 2), 0] : [r, r, 0];
    }
    case 'port':
      return ft.diameterMm ? [ft.diameterMm / 2 + 6, ft.diameterMm / 2 + 6, 0] : [(ft.slot?.w ?? 0) / 2, (ft.slot?.h ?? 0) / 2, 0];
    case 'panel':
      return [ft.w / 2, ft.h / 2, 0];
    case 'knob': {
      const r = ft.diameterMm / 2;
      const extra = ft.repeat ? (ft.repeat.count - 1) * ft.repeat.pitchMm : 0;
      return [r, r, extra];
    }
    case 'lens': {
      const r = ft.diameterMm / 2;
      return [r, r, 0];
    }
  }
}

/** How far (mm) a feature stands proud of its face. `recessPanels`: the body is
 *  a box, which carves a cavity for a panel with a negative depthMm; other
 *  bodies lay that panel flush. */
export function featureProtrusion(ft: RecipeFeature, recessPanels = false): number {
  switch (ft.kind) {
    case 'driver':
      return driverProfile(ft).protrusion;
    case 'port':
      return ft.diameterMm ? PORT_RIM : FLUSH;
    case 'panel':
      // A recessed panel (depthMm < 0) doesn't stand proud at all — it sinks
      // into a cavity the box-body branch carves for it (see the `isRecess`
      // handling in buildRecipe()'s panel case), so it must not pull the
      // cabinet's own face in by the flush floor the way a proud/flush panel
      // does. Leaving this at Math.max(depthMm, FLUSH) for a negative depthMm
      // silently pulled the cabinet's true face in by FLUSH (0.6mm) even
      // though the skin above was built out to the real face — the
      // recessed picture panel of a TV therefore built 0.6mm short of the
      // published body depth on every face carrying one.
      return recessPanels && ft.depthMm < 0 ? 0 : Math.max(ft.depthMm, FLUSH);
    case 'knob':
      return ft.depthMm;
    case 'lens':
      return Math.max(ft.protrudeMm, FLUSH);
    case 'feet':
      return 0;
  }
}

const FLUSH = 0.6;
const PORT_RIM = 2.5;

interface DriverProfile {
  ring: [number, number][];
  surround: [number, number][];
  cone: [number, number][];
  protrusion: number;
}

function driverProfile(ft: Extract<RecipeFeature, { kind: 'driver' }>): DriverProfile {
  const R = ft.outerMm / 2;
  // Visible diaphragm radius: the nominal size minus the frame lip, never past the ring.
  const rc = Math.min((ft.nominalMm / 2) * 0.82, R * 0.86);
  const ringH = 3;
  // Thin trim band at the edge; the surround fills the rest down to the diaphragm.
  const band = Math.max(1.5, R * 0.1);
  const ring: [number, number][] = [
    [R - band, ringH * 0.6],
    [R, 0],
    [R, ringH * 0.5],
    [R * 0.97, ringH],
    [R - band, ringH],
  ];
  const surround: [number, number][] = [
    [rc, ringH * 0.4],
    [R - band + 0.2, ringH * 0.55],
  ];
  if (ft.style === 'dome') {
    const dome = Math.min(rc * 0.55, 14);
    const cone: [number, number][] = [];
    for (let i = 0; i <= 8; i += 1) {
      const a = (i / 8) * (Math.PI / 2);
      cone.push([rc * Math.cos(a), ringH * 0.4 + dome * Math.sin(a)]);
    }
    cone.push([0, ringH * 0.4 + dome]);
    return { ring, surround, cone: cone.reverse(), protrusion: Math.max(ringH, ringH * 0.4 + dome) };
  }
  if (ft.style === 'cone') {
    // Shallow dish with a dust cap: rim level with the ring, centre dished, cap proud of the centre.
    const cap = rc * 0.3;
    const cone: [number, number][] = [
      [0, ringH * 0.9],
      [cap * 0.7, ringH * 0.85],
      [cap, ringH * 0.5],
      [cap * 1.05, 0.8],
      [rc, ringH * 0.5],
    ];
    return { ring, surround, cone, protrusion: ringH };
  }
  // horn / ribbon: a small dome; the mouth or plate is added separately as a panel.
  const dome = Math.min(rc * 0.45, 8);
  return {
    ring,
    surround,
    cone: [
      [0, ringH * 0.4 + dome],
      [rc * 0.7, ringH * 0.4 + dome * 0.7],
      [rc, ringH * 0.4],
    ],
    protrusion: Math.max(ringH, ringH * 0.4 + dome, 5),
  };
}

export function buildRecipe(recipe: Recipe): BuiltRecipe {
  const b = recipe.body;
  const W = b.shape === 'round_in_ceiling' ? b.diameter : b.w;
  const H = b.shape === 'round_in_ceiling' ? b.diameter : b.shape === 'wedge' ? Math.max(b.h, b.frontH) : b.h;
  const D = b.d;
  // The cabinet above the feet may be narrower than the overall footprint (plinth, outriggers).
  const CW = b.shape === 'box' && b.cabinet ? b.cabinet.w : W;
  const CD = b.shape === 'box' && b.cabinet ? b.cabinet.d : D;

  const prims: Primitive[] = [];
  const feet = recipe.features.find((f): f is Extract<RecipeFeature, { kind: 'feet' }> => f.kind === 'feet');
  const feetH = feet?.heightMm ?? 0;

  // Panels (baffles, trim plates) sit on the face; drivers, ports and knobs
  // sit on top of the deepest panel of their face. The largest protrusion per
  // face pulls the cabinet in on that face.
  // Panels stack in array order: a panel rests on every earlier panel it overlaps.
  const panelBase = new Map<RecipeFeature, number>();
  const lift: Record<Face, number> = { front: 0, top: 0, rear: 0, left: 0, right: 0, slope: 0 };
  const placedPanels: { ft: Extract<RecipeFeature, { kind: 'panel' }>; top: number }[] = [];
  for (const ft of recipe.features) {
    if (ft.kind !== 'panel') continue;
    const base = placedPanels
      .filter((q) => q.ft.face === ft.face && panelsOverlap(q.ft, ft, recipe))
      .reduce((m, q) => Math.max(m, q.top), 0);
    const top = base + featureProtrusion(ft, b.shape === 'box');
    panelBase.set(ft, base);
    placedPanels.push({ ft, top });
    lift[ft.face] = Math.max(lift[ft.face], top);
  }
  const pro: Record<Face, number> = { ...lift };
  for (const ft of recipe.features) {
    if (ft.kind === 'feet' || ft.kind === 'panel') continue;
    // A horn or ribbon plate carries its dome half-way up the plate.
    const own = ft.kind === 'driver' ? driverOwnProtrusion(ft, b.shape === 'box') : featureProtrusion(ft);
    pro[ft.face] = Math.max(pro[ft.face], lift[ft.face] + own);
  }

  const cab: CabinetBox = {
    x0: -CW / 2 + pro.left,
    x1: CW / 2 - pro.right,
    y0: feetH,
    y1: H - pro.top,
    yf: H - pro.top,
    yr: H - pro.top,
    z0: -CD / 2 + pro.rear,
    z1: CD / 2 - pro.front,
  };
  if (b.shape === 'wedge') {
    cab.yf = b.frontH;
    cab.yr = b.h;
    cab.y1 = H;
    // Slope features stand proud of the slope along its normal. Lower the slope
    // and pull in the front or rear face only by as much as the outermost
    // feature point would overhang the published envelope, so the model is
    // exactly W × H × D whether or not a feature reaches an edge. A few passes
    // converge because each correction only slightly moves the slope.
    const slopeFeatures = recipe.features.filter(
      (f): f is Exclude<RecipeFeature, { kind: 'feet' }> => f.kind !== 'feet' && f.face === 'slope',
    );
    for (let pass = 0; pass < 4 && slopeFeatures.length > 0; pass += 1) {
      const f = faceFrame('slope', cab);
      let maxY = -Infinity;
      let maxZ = -Infinity;
      let minZ = Infinity;
      for (const ft of slopeFeatures) {
        const [hx, hy, extra] = featureHalfExtents(ft);
        const out = ft.kind === 'panel'
          ? (panelBase.get(ft) ?? 0) + featureProtrusion(ft)
          : lift.slope + (ft.kind === 'driver' ? driverOwnProtrusion(ft, false) : featureProtrusion(ft));
        for (const dv of [-hy, hy]) {
          for (const du of [-hx, hx + extra]) {
            const pt = facePoint(f, ft.u + du / f.width, ft.v + dv / f.height, out);
            maxY = Math.max(maxY, pt[1]);
            maxZ = Math.max(maxZ, pt[2]);
            minZ = Math.min(minZ, pt[2]);
          }
        }
      }
      const dy = Math.max(0, maxY - H);
      const dzFront = Math.max(0, maxZ - D / 2);
      const dzRear = Math.max(0, -D / 2 - minZ);
      if (dy < 1e-6 && dzFront < 1e-6 && dzRear < 1e-6) break;
      cab.yf -= dy;
      cab.yr -= dy;
      cab.y1 = Math.max(cab.yf, cab.yr);
      cab.z1 -= dzFront;
      cab.z0 += dzRear;
    }
  }
  const hornFeatures = b.shape === 'box'
    ? recipe.features.filter((f): f is Extract<RecipeFeature, { kind: 'driver' }> => f.kind === 'driver' && f.style === 'horn' && !!f.mouth)
    : [];
  const hornFaces = [...new Set(hornFeatures.map((f) => f.face))];
  /** Horn mouths on a face, in face millimetres (x across from centre, y up from the bottom edge). */
  const hornHoles = (face: Face, f: FaceFrame): Rect[] =>
    hornFeatures
      .filter((ft) => ft.face === face && ft.mouth)
      .map((ft) => {
        const cx = ft.u * f.width;
        const cy = ft.v * f.height;
        const m = ft.mouth!;
        return { x0: cx - m.w / 2, x1: cx + m.w / 2, y0: cy - m.h / 2, y1: cy + m.h / 2 };
      });
  const cabSize: Vec3 = [cab.x1 - cab.x0, cab.y1 - cab.y0, cab.z1 - cab.z0];
  const cabAt: Vec3 = [(cab.x0 + cab.x1) / 2, (cab.y0 + cab.y1) / 2, (cab.z0 + cab.z1) / 2];
  const world: Basis = { x: [1, 0, 0], y: [0, 1, 0], z: [0, 0, 1] };

  if (b.shape === 'wedge') {
    // Side profile in (z, y) extruded across the width, right to left:
    // basis x = world z, y = world y, z = world -x keeps the frame right-handed.
    prims.push({
      shape: 'prism',
      points: [[cab.z0, cab.y0], [cab.z1, cab.y0], [cab.z1, cab.yf], [cab.z0, cab.yr]],
      depth: cab.x1 - cab.x0,
      at: [cab.x1, 0, 0],
      basis: { x: [0, 0, 1], y: [0, 1, 0], z: [-1, 0, 0] },
      material: b.material,
    });
  } else if (b.shape === 'box') {
    // A horn is a cavity in the baffle; so is a recessed panel (depthMm < 0
    // — the picture area of a TV, sunk behind its bezel). Faces carrying
    // either are built as a skin with the cavity mouths cut out, over a main
    // box pulled in by the same depth; the flare or recessed plate sits at
    // the bottom of the cavity.
    const recessFaces = [...new Set(
      recipe.features
        .filter((ft): ft is Extract<RecipeFeature, { kind: 'panel' }> => ft.kind === 'panel' && ft.depthMm < 0)
        .map((ft) => ft.face),
    )];
    const recessDepth = (face: Face) =>
      Math.max(
        0,
        ...recipe.features
          .filter((ft): ft is Extract<RecipeFeature, { kind: 'panel' }> => ft.kind === 'panel' && ft.face === face && ft.depthMm < 0)
          .map((ft) => -ft.depthMm),
      );
    const skinFaces = [...new Set([...hornFaces, ...recessFaces])];
    const skinDepth = (face: Face) => Math.max(hornFaces.includes(face) ? HORN_DEPTH : 0, recessDepth(face));
    const recessHoles = (face: Face, f: FaceFrame): Rect[] =>
      recipe.features
        .filter((ft): ft is Extract<RecipeFeature, { kind: 'panel' }> => ft.kind === 'panel' && ft.face === face && ft.depthMm < 0)
        .map((ft) => {
          const cx = ft.u * f.width;
          const cy = ft.v * f.height;
          return { x0: cx - ft.w / 2, x1: cx + ft.w / 2, y0: cy - ft.h / 2, y1: cy + ft.h / 2 };
        });

    const main = { ...cab };
    for (const face of skinFaces) {
      const d = skinDepth(face);
      if (face === 'front') main.z1 -= d;
      else if (face === 'rear') main.z0 += d;
      else if (face === 'left') main.x0 += d;
      else if (face === 'right') main.x1 -= d;
      else if (face === 'top') main.y1 -= d;
    }
    const mainSize: Vec3 = [main.x1 - main.x0, main.y1 - main.y0, main.z1 - main.z0];
    prims.push({
      shape: 'roundedBox',
      size: mainSize,
      radius: Math.min(b.edgeRadius, Math.min(...mainSize) / 2),
      sideRadius: Math.min(b.sideRadius ?? 0, Math.min(mainSize[0], mainSize[2]) / 2),
      at: [(main.x0 + main.x1) / 2, (main.y0 + main.y1) / 2, (main.z0 + main.z1) / 2],
      basis: world,
      material: b.material,
    });
    for (const face of skinFaces) {
      const f = faceFrame(face, cab);
      const holes = [...hornHoles(face, f), ...recessHoles(face, f)];
      const d = skinDepth(face);
      for (const r of rectMinusHoles({ x0: -f.width / 2, x1: f.width / 2, y0: 0, y1: f.height }, holes)) {
        prims.push(rectPlate(f, r, d, -d, b.material));
      }
    }
  } else {
    // Trim disc at the front, can behind it.
    const r = b.diameter / 2;
    const zFront = cab.z1;
    const zTrim = zFront - b.trimMm;
    const zBack = cab.z0;
    const toFront: Basis = { x: [1, 0, 0], y: [0, 0, 1], z: [0, -1, 0] };
    prims.push({
      shape: 'lathe',
      points: [[0, 0], [r, 0], [r, b.trimMm * 0.6], [r * 0.96, b.trimMm], [0, b.trimMm]],
      at: [0, H / 2, zTrim],
      basis: toFront,
      material: b.material,
    });
    const can = r * 0.82;
    prims.push({
      shape: 'lathe',
      points: [[0, 0], [can, 0], [can, zTrim - zBack], [0, zTrim - zBack]],
      at: [0, H / 2, zBack],
      basis: toFront,
      material: DARK,
    });
  }

  for (const ft of recipe.features) {
    if (ft.kind === 'feet') {
      prims.push(...feetPrimitives(ft, W, D, CW, CD));
      continue;
    }
    const f = faceFrame(ft.face, cab);
    switch (ft.kind) {
      case 'driver': {
        const prof = driverProfile(ft);
        const isHorn = ft.style === 'horn' && ft.mouth;
        const recessed = isHorn && b.shape === 'box';
        const isRibbon = ft.style === 'ribbon' && ft.mouth;
        // A recessed horn's dome sits at the bottom of its cavity; an outward
        // flare's at the face; a ribbon's half-way up its plate.
        const at = facePoint(f, ft.u, ft.v, recessed ? -HORN_DEPTH : lift[ft.face] + (isRibbon ? MOUTH_DEPTH / 2 : 0));
        if (isHorn && ft.mouth) {
          // Square flare from the throat (the driver's trim) to the mouth. On a box
          // it runs up from the bottom of the cavity to flush with the face's
          // plates; on a wedge or round trim it stands proud. The sloped walls
          // catch the light, so the horn reads as a horn.
          const throat = Math.min(ft.outerMm, ft.mouth.w, ft.mouth.h);
          // A square mouth is a 4-segment lathe whose corners touch the unit
          // square; a round mouth is a smooth lathe of radius 1/2.
          const half = ft.mouth.round ? 0.5 : Math.SQRT2 / 2;
          const n = latheBasis(f).y;
          prims.push({
            shape: 'lathe',
            points: [[half * (throat / ft.mouth.w), 0], [half, recessed ? HORN_DEPTH + lift[ft.face] : HORN_DEPTH]],
            segments: ft.mouth.round ? 32 : 4,
            phiStart: ft.mouth.round ? 0 : Math.PI / 4,
            at,
            // Unit square in the lathe plane scaled to the mouth: x along u by w, z along v by h.
            basis: { x: scale(f.u, ft.mouth.w), y: n, z: scale(cross(f.u, n), ft.mouth.h) },
            material: ft.surround ?? SURROUND,
          });
        }
        if (isRibbon && ft.mouth) {
          prims.push(plate(f, ft.u, ft.v, ft.mouth.w, ft.mouth.h, MOUTH_DEPTH, lift[ft.face], Math.min(ft.mouth.w, ft.mouth.h) * 0.12, ft.surround ?? SURROUND));
        }
        prims.push({ shape: 'lathe', points: prof.ring, at, basis: latheBasis(f), material: ft.ring });
        prims.push({ shape: 'lathe', points: prof.surround, at, basis: latheBasis(f), material: ft.surround ?? SURROUND });
        prims.push({ shape: 'lathe', points: prof.cone, at, basis: latheBasis(f), material: ft.cone });
        break;
      }
      case 'port': {
        const at = facePoint(f, ft.u, ft.v, lift[ft.face]);
        if (ft.diameterMm) {
          const r = ft.diameterMm / 2;
          prims.push({
            shape: 'lathe',
            points: [[r, 0], [r + 6, 0], [r + 6, PORT_RIM * 0.6], [r + 4, PORT_RIM], [r, PORT_RIM]],
            at,
            basis: latheBasis(f),
            material: DARK,
          });
          prims.push({ shape: 'lathe', points: [[0, 0.3], [r, 0.3]], at, basis: latheBasis(f), material: DARK });
        } else if (ft.slot) {
          prims.push(plate(f, ft.u, ft.v, ft.slot.w, ft.slot.h, FLUSH, lift[ft.face], Math.min(ft.slot.w, ft.slot.h) * 0.12, DARK));
        }
        break;
      }
      case 'panel': {
        // A negative depthMm on a box body sits in the cavity the skin above
        // carved out for it: a thin plate whose outward face is exactly
        // depthMm below the true face, so the surrounding bezel reads as a
        // visible step. Elsewhere (proud/flush panels, or any panel on a
        // non-box body, which has no cavity to sink into) depthMm still only
        // sets thickness, clamped to a visible minimum.
        const isRecess = b.shape === 'box' && ft.depthMm < 0;
        const t = isRecess ? FLUSH : Math.max(ft.depthMm, FLUSH);
        // The plate RESTS ON the cavity floor (base = its own depth) rather
        // than ending at it. Ending at it put the plate's outward face in the
        // same plane as the main box's face for the deepest panel of a face —
        // two coplanar surfaces, which z-fight across the whole picture area
        // of every TV and screen.
        const base = isRecess ? ft.depthMm : (panelBase.get(ft) ?? 0);
        const holes = hornFaces.includes(ft.face) ? hornHoles(ft.face, f) : [];
        const cx = ft.u * f.width;
        const cy = ft.v * f.height;
        const rect = { x0: cx - ft.w / 2, x1: cx + ft.w / 2, y0: cy - ft.h / 2, y1: cy + ft.h / 2 };
        if (holes.some((h) => h.x0 < rect.x1 && h.x1 > rect.x0 && h.y0 < rect.y1 && h.y1 > rect.y0)) {
          // A plate a horn is cut through: square pieces around the mouth.
          for (const r of rectMinusHoles(rect, holes)) prims.push(rectPlate(f, r, t, base, ft.material));
        } else {
          prims.push(plate(f, ft.u, ft.v, ft.w, ft.h, t, base, ft.radiusMm, ft.material));
        }
        break;
      }
      case 'knob': {
        const count = ft.repeat?.count ?? 1;
        const pitch = ft.repeat?.pitchMm ?? 0;
        for (let i = 0; i < count; i += 1) {
          const du = (i * pitch) / f.width;
          const r = ft.diameterMm / 2;
          prims.push({
            shape: 'lathe',
            points: [[0, 0], [r, 0], [r, ft.depthMm * 0.85], [r * 0.92, ft.depthMm], [0, ft.depthMm]],
            at: facePoint(f, ft.u + du, ft.v, lift[ft.face]),
            basis: latheBasis(f),
            material: ft.material,
          });
        }
        break;
      }
      case 'lens': {
        const R = ft.diameterMm / 2;
        const out = Math.max(ft.protrudeMm, FLUSH);
        const at = facePoint(f, ft.u, ft.v, lift[ft.face]);
        // Barrel: a straight cylinder, very slightly tapered at the rim so it
        // catches light as a distinct surface from the glass front.
        prims.push({
          shape: 'lathe',
          points: [[R, 0], [R, out * 0.85], [R * 0.94, out]],
          at,
          basis: latheBasis(f),
          material: ft.barrel,
        });
        // Glass front: a flat disc capping the barrel.
        prims.push({
          shape: 'lathe',
          points: [[0, out], [R * 0.86, out]],
          at,
          basis: latheBasis(f),
          material: ft.glass,
        });
        break;
      }
    }
  }

  return { primitives: prims, size: [W, H, D] };
}

const MOUTH_DEPTH = 4;
const HORN_DEPTH = 14;

/** How far a driver stands proud of its face, including a horn flare or ribbon plate.
 *  `recessHorns`: the body is a box, so a horn is a cavity (its dome sits
 *  HORN_DEPTH below the face); on other bodies the flare stands proud. */
function driverOwnProtrusion(ft: Extract<RecipeFeature, { kind: 'driver' }>, recessHorns: boolean): number {
  if (ft.style === 'horn' && ft.mouth) {
    return recessHorns ? Math.max(0, featureProtrusion(ft) - HORN_DEPTH) : Math.max(HORN_DEPTH, featureProtrusion(ft));
  }
  if (ft.style === 'ribbon' && ft.mouth) return Math.max(MOUTH_DEPTH, MOUTH_DEPTH / 2 + featureProtrusion(ft));
  return featureProtrusion(ft);
}

/** Do two panels on the same face overlap (in face millimetres)? */
function panelsOverlap(
  a: Extract<RecipeFeature, { kind: 'panel' }>,
  b: Extract<RecipeFeature, { kind: 'panel' }>,
  recipe: Recipe,
): boolean {
  const body = recipe.body;
  const W = body.shape === 'round_in_ceiling' ? body.diameter : body.w;
  const H = body.shape === 'round_in_ceiling' ? body.diameter : body.h;
  const D = body.d;
  const [fw, fh] = a.face === 'left' || a.face === 'right' ? [D, H] : a.face === 'top' ? [W, D] : [W, H];
  return (
    Math.abs(a.u * fw - b.u * fw) < (a.w + b.w) / 2 && Math.abs(a.v * fh - b.v * fh) < (a.h + b.h) / 2
  );
}

interface Rect {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

/** `rect` minus the union of `holes`, as axis-aligned rectangles (column decomposition). */
export function rectMinusHoles(rect: Rect, holes: Rect[]): Rect[] {
  const xs = [...new Set([rect.x0, rect.x1, ...holes.flatMap((h) => [h.x0, h.x1])])]
    .filter((x) => x >= rect.x0 && x <= rect.x1)
    .sort((a, b) => a - b);
  const out: Rect[] = [];
  for (let i = 0; i + 1 < xs.length; i += 1) {
    const [xa, xb] = [xs[i], xs[i + 1]];
    if (xb - xa < 1e-6) continue;
    const cuts = holes
      .filter((h) => h.x0 <= xa + 1e-6 && h.x1 >= xb - 1e-6)
      .map((h) => [Math.max(h.y0, rect.y0), Math.min(h.y1, rect.y1)] as const)
      .filter(([a, c]) => c > a)
      .sort((p, q) => p[0] - q[0]);
    let y = rect.y0;
    for (const [a, c] of cuts) {
      if (a > y + 1e-6) out.push({ x0: xa, x1: xb, y0: y, y1: a });
      y = Math.max(y, c);
    }
    if (rect.y1 > y + 1e-6) out.push({ x0: xa, x1: xb, y0: y, y1: rect.y1 });
  }
  return out;
}

/** A square-cornered plate covering face rectangle `r` (face millimetres), `t` thick, starting `base` above the face. */
function rectPlate(f: FaceFrame, r: Rect, t: number, base: number, material: RecipeMaterial): Primitive {
  return plate(f, (r.x0 + r.x1) / 2 / f.width, (r.y0 + r.y1) / 2 / f.height, r.x1 - r.x0, r.y1 - r.y0, t, base, 0, material);
}

/** A w × h plate of thickness t lying on a face, rounded in-plane by `radius`. */
function plate(f: FaceFrame, u: number, v: number, w: number, h: number, t: number, base: number, radius: number, material: RecipeMaterial): Primitive {
  return {
    shape: 'roundedBox',
    // Basis y is the face normal so sideRadius rounds the plate's in-plane corners.
    size: [w, t, h],
    radius: 0,
    sideRadius: Math.max(0, Math.min(radius, w / 2, h / 2)),
    at: facePoint(f, u, v, base + t / 2),
    basis: { x: f.u, y: f.n, z: cross(f.u, f.n) },
    material,
  };
}

/** Plinth and outriggers span the overall footprint W × D; pads sit under the cabinet CW × CD. */
function feetPrimitives(ft: Extract<RecipeFeature, { kind: 'feet' }>, W: number, D: number, CW: number, CD: number): Primitive[] {
  const world: Basis = { x: [1, 0, 0], y: [0, 1, 0], z: [0, 0, 1] };
  const h = ft.heightMm;
  const fw = Math.min(ft.w ?? W, W);
  const fd = Math.min(ft.d ?? D, D);
  if (ft.style === 'plinth') {
    return [{ shape: 'roundedBox', size: [fw, h, fd], radius: Math.min(2, h / 2), sideRadius: 0, at: [0, h / 2, 0], basis: world, material: ft.material }];
  }
  if (ft.style === 'outriggers') {
    const bar = Math.max(12, Math.min(40, fd * 0.12));
    return [-1, 1].map((s) => ({
      shape: 'roundedBox' as const,
      size: [fw, h, bar] as Vec3,
      radius: Math.min(2, h / 2),
      sideRadius: 0,
      at: [0, h / 2, s * (fd / 2 - bar / 2)] as Vec3,
      basis: world,
      material: ft.material,
    }));
  }
  const r = Math.max(6, Math.min(CW, CD) * 0.07);
  const xs = CW / 2 - r - CW * 0.06;
  const zs = CD / 2 - r - CD * 0.06;
  return [
    [-xs, -zs],
    [xs, -zs],
    [-xs, zs],
    [xs, zs],
  ].map(([x, z]) => ({
    shape: 'lathe' as const,
    points: [[0, 0], [r, 0], [r, h], [0, h]] as [number, number][],
    at: [x, 0, z] as Vec3,
    basis: world,
    material: ft.material,
  }));
}
